import {create} from 'zustand';
import {Visit, VisitStatistics, VisitFilters, VisitTrends} from '../models/visit.model';
import {Pagination} from '../../../Data/types/BaseResponse';
import {VisitsFactory} from './visitsFactory';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {VisitAPIDataSource} from '../api/VisitAPIDataSource';
import type {VisitActionResponse} from '../api/VisitAPIDataSourceManagement';

interface VisitsState {
  // Data
  visits: Visit[];
  visitDetails: Visit | null;
  statistics: VisitStatistics | null;
  trends: VisitTrends | null;
  pagination: Pagination | undefined;

  // Filters
  filters: VisitFilters;

  // Loading states
  loading: boolean;
  loadingDetails: boolean;
  loadingStatistics: boolean;
  loadingTrends: boolean;

  // Error states
  error: string | null;
  detailsError: string | null;
  statisticsError: string | null;
  trendsError: string | null;

  // Actions
  fetchVisits: (filters?: VisitFilters, page?: number, limit?: number) => Promise<void>;
  fetchVisitDetails: (visitId: string | number) => Promise<void>;
  fetchStatistics: () => Promise<void>;
  fetchTrends: () => Promise<void>;

  // Management actions
  approveVisit: (visitId: string | number) => Promise<void>;
  rejectVisit: (visitId: string | number, reason: string) => Promise<void>;

  // Utility actions
  setFilters: (filters: VisitFilters) => void;
  setPagination: (pagination: Pagination | undefined) => void;
  refreshData: () => Promise<void>;
  clearErrors: () => void;
  clearVisitDetails: () => void;
}

export const useVisitsStore = create<VisitsState>((set, get) => {
  // Initialize use cases using factory
  const getVisitsList = VisitsFactory.createGetVisitsListUseCase();
  const getVisitDetails = VisitsFactory.createGetVisitDetailsUseCase();
  const getStatistics = VisitsFactory.createGetVisitsOverviewUseCase();
  const getTrends = VisitsFactory.createGetVisitTrendsUseCase();
  // manageVisit removed - management functionality not needed in dashboard
  const errorHandlingService = StandardizedErrorHandler.getInstance();

  return {
    // Initial state
    visits: [],
    visitDetails: null,
    statistics: null,
    trends: null,
    pagination: undefined,
    filters: {status: 'pending'},
    loading: false,
    loadingDetails: false,
    loadingStatistics: false,
    loadingTrends: false,
    error: null,
    detailsError: null,
    statisticsError: null,
    trendsError: null,

    // Actions
    fetchVisits: async (filters = get().filters, page = 1, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE) => {
      set({loading: true, error: null});
      try {
        const {visits, pagination} = await getVisitsList.execute({...filters, page, limit});
        set({visits, pagination, loading: false, filters, error: null});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    fetchVisitDetails: async (visitId: string | number) => {
      set({loadingDetails: true, detailsError: null});
      try {
        const visitDetails = await getVisitDetails.execute(visitId);
        set({visitDetails, loadingDetails: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({detailsError: appError.message, loadingDetails: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    fetchStatistics: async () => {
      set({loadingStatistics: true, statisticsError: null});
      try {
        const statistics = await getStatistics.execute();
        set({statistics, loadingStatistics: false, statisticsError: null});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({statisticsError: appError.message, loadingStatistics: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    fetchTrends: async () => {
      set({loadingTrends: true, trendsError: null});
      try {
        const trends = await getTrends.execute();
        set({trends, loadingTrends: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({trendsError: appError.message, loadingTrends: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    // Management actions
    approveVisit: async (visitId: string | number) => {
      try {
        if (!visitId) {
          throw new Error('Visit ID is required for approval');
        }
        const response: VisitActionResponse = await VisitAPIDataSource.approveVisit(visitId);

        if (!response) {
          throw new Error('No response from approve API');
        }

        // API returns minimal response with visit_id and status
        // Fetch full visit details to update the store properly
        const responseVisitId = response.visit_id || visitId;
        const fullVisitDetails = await getVisitDetails.execute(responseVisitId);

        // Update the visit in the visits list and details
        set((state) => ({
          visits: state.visits.map(visit =>
            visit.id === fullVisitDetails.id ? fullVisitDetails : visit
          ),
          visitDetails: state.visitDetails?.id === fullVisitDetails.id ? fullVisitDetails : state.visitDetails
        }));

      } catch (err) {
        console.error('Store: Error in approveVisit:', err);
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message});
        errorHandlingService.handleError(err);
        throw appError;
      }
    },

    rejectVisit: async (visitId: string | number, reason: string) => {
      try {
        if (!visitId) {
          throw new Error('Visit ID is required for rejection');
        }
        const response: VisitActionResponse = await VisitAPIDataSource.rejectVisit(visitId, reason);

        if (!response) {
          throw new Error('No response from reject API');
        }

        // API returns minimal response with visit_id and status
        // Fetch full visit details to update the store properly
        const responseVisitId = response.visit_id || visitId;
        const fullVisitDetails = await getVisitDetails.execute(responseVisitId);

        // Update the visit in the visits list and details
        set((state) => ({
          visits: state.visits.map(visit =>
            visit.id === fullVisitDetails.id ? fullVisitDetails : visit
          ),
          visitDetails: state.visitDetails?.id === fullVisitDetails.id ? fullVisitDetails : state.visitDetails
        }));

      } catch (err) {
        console.error('Store: Error in rejectVisit:', err);
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message});
        errorHandlingService.handleError(err);
        throw appError;
      }
    },

    // Utility actions
    setFilters: (filters: VisitFilters) => {
      set({filters});
    },

    setPagination: (pagination: Pagination | undefined) => {
      set({pagination});
    },

    refreshData: async () => {
      const currentState = get();
      const page = currentState.pagination?.page || 1;
      const limit = currentState.pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
      await get().fetchVisits(currentState.filters, page, limit);
    },

    clearErrors: () => {
      set({
        error: null,
        detailsError: null,
        statisticsError: null,
        trendsError: null
      });
    },

    clearVisitDetails: () => {
      set({visitDetails: null, detailsError: null});
    }
  };
});
